import { useEffect, useState } from "react";
import { api, downloadFile } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDown } from "lucide-react";
import { toast } from "sonner";

const th = "border px-2 py-1.5 text-[11px] font-bold bg-slate-200 dark:bg-slate-700 text-center";
const td = "border px-2 py-1.5 text-center";

function ReportTable({ mx }) {
  const ings = mx.ing_cols.length ? mx.ing_cols : ["-"];
  const sts = mx.stage_cols.length ? mx.stage_cols : ["PROSES"];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border" data-testid="pr-table">
        <thead>
          <tr>
            <th rowSpan={2} className={th}>TANGGAL</th><th rowSpan={2} className={th}>BATCH</th>
            <th colSpan={ings.length} className={th}>BERAT BAHAN YANG DIGUNAKAN UNTUK PRODUKSI</th>
            {sts.map((s) => <th key={s} colSpan={2} className={th}>{s}</th>)}
            <th rowSpan={2} className={th}>JUMLAH PRODUK JADI</th><th rowSpan={2} className={th}>PARAF</th><th rowSpan={2} className={th}>NO. TRACKING</th>
          </tr>
          <tr>
            {ings.map((c) => <th key={c} className={th}>{c}</th>)}
            {sts.map((s) => [<th key={`${s}-a`} className={th}>WAKTU MULAI</th>, <th key={`${s}-b`} className={th}>WAKTU SELESAI</th>])}
          </tr>
        </thead>
        <tbody>
          {mx.rows.length === 0 && <tr><td colSpan={5 + ings.length + sts.length * 2} className="text-center py-6 text-muted-foreground" data-testid="pr-empty">Belum ada pencatatan untuk merek ini.</td></tr>}
          {mx.rows.map((r) => (
            <tr key={r.id} data-testid={`pr-row-${r.tracking_no}`}>
              <td className={td}>{r.tanggal}</td><td className={`${td} font-bold`}>{r.batch}</td>
              {ings.map((c) => <td key={c} className={td}>{r.ings[c] || "-"}</td>)}
              {sts.map((s) => { const v = r.stages[s] || ["-", "-"]; return [<td key={`${s}-a`} className={td}>{v[0]}</td>, <td key={`${s}-b`} className={td}>{v[1]}</td>]; })}
              <td className={`${td} font-bold`}>{r.jumlah}</td><td className={`${td} font-bold`}>{r.paraf}</td><td className={`${td} font-mono`}>{r.tracking_no}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ProductionReport() {
  const [mereks, setMereks] = useState([]);
  const [f, setF] = useState({ merek: "", date_from: "", date_to: "" });
  const [mx, setMx] = useState(null);

  useEffect(() => { api.get("/production-mereks").then((r) => setMereks(r.data)); }, []);

  const params = () => Object.fromEntries(Object.entries(f).filter(([, v]) => v));
  useEffect(() => {
    if (!f.merek) return;
    api.get("/production-report", { params: params() }).then((r) => setMx(r.data)).catch(() => toast.error("Gagal memuat laporan."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f]);

  async function dl() {
    try {
      await downloadFile(`/reports/mj-form-23-merek.pdf?${new URLSearchParams(params())}`, `MJ-FORM-23_${f.merek}.pdf`);
      toast.success("PDF berhasil diunduh.");
    } catch { toast.error("Gagal mengunduh PDF."); }
  }

  return (
    <div className="space-y-5">
      <div><h1 className="font-heading text-2xl font-extrabold">MJ-FORM-23 — Laporan per Merek</h1>
        <p className="text-sm text-muted-foreground">Setiap merek memiliki bahan dan tahapan proses sendiri. Pilih merek untuk melihat laporan, lalu cetak PDF A4.</p></div>
      <Card className="p-5 space-y-4">
        <div className="grid sm:grid-cols-3 gap-4">
          <div><Label className="text-xs">Merek</Label>
            <Select value={f.merek} onValueChange={(v) => setF({ ...f, merek: v })}>
              <SelectTrigger data-testid="pr-merek" className="mt-1"><SelectValue placeholder="Pilih merek" /></SelectTrigger>
              <SelectContent>{mereks.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Dari Tanggal</Label><Input type="date" data-testid="pr-from" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Sampai Tanggal</Label><Input type="date" data-testid="pr-to" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="mt-1" /></div>
        </div>
        <Button data-testid="pr-dl-pdf" disabled={!f.merek} onClick={dl}><FileDown className="w-4 h-4 mr-1" /> Cetak PDF A4</Button>
      </Card>
      {mx && f.merek && (
        <Card className="p-5 space-y-3" data-testid="pr-preview">
          <div className="text-center">
            <div className="font-heading font-extrabold">PT TIGAPUTRA BIOTECH SEJAHTERA</div>
            <div className="font-bold text-sm" data-testid="pr-title">FORMULIR PENCATATAN PROSES ({f.merek.toUpperCase()})</div>
          </div>
          <ReportTable mx={mx} />
        </Card>
      )}
    </div>
  );
}
